import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { 
  CalendarDays, 
  Users, 
  Plus, 
  X, 
  RefreshCw,
  Download,
  Utensils,
  Coffee,
  Sparkles,
  ClipboardList,
  Trash2
} from 'lucide-react';

const DAYS = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'];

const DEFAULT_DUTIES = [
  { id: 'fruehstueck', name: 'Frühstück decken & aufräumen', color: 'bg-amber-100 text-amber-700 border-amber-300' },
  { id: 'kochen', name: 'Essen kochen', color: 'bg-red-100 text-red-700 border-red-300' },
  { id: 'abdecken', name: 'Essen abdecken & sauber machen', color: 'bg-blue-100 text-blue-700 border-blue-300' }
];

const COLORS = [
  'bg-amber-100 text-amber-700 border-amber-300',
  'bg-red-100 text-red-700 border-red-300',
  'bg-blue-100 text-blue-700 border-blue-300',
  'bg-green-100 text-green-700 border-green-300',
  'bg-purple-100 text-purple-700 border-purple-300',
  'bg-pink-100 text-pink-700 border-pink-300',
  'bg-indigo-100 text-indigo-700 border-indigo-300',
  'bg-teal-100 text-teal-700 border-teal-300',
];

const DienstplanGenerator = ({ open, onClose }) => {
  const [trainees, setTrainees] = useState([]);
  const [newTraineeName, setNewTraineeName] = useState('');
  const [duties, setDuties] = useState(DEFAULT_DUTIES);
  const [newDutyName, setNewDutyName] = useState('');
  const [schedule, setSchedule] = useState(null);
  const [error, setError] = useState('');
  const [stats, setStats] = useState(null);

  const addTrainee = () => {
    if (!newTraineeName.trim()) return;
    if (trainees.includes(newTraineeName.trim())) {
      setError('Name bereits vorhanden');
      return;
    }
    setTrainees([...trainees, newTraineeName.trim()]);
    setNewTraineeName('');
    setError('');
    setSchedule(null);
    setStats(null);
  };

  const removeTrainee = (name) => {
    setTrainees(trainees.filter(t => t !== name));
    setSchedule(null);
    setStats(null);
  };

  const addDuty = () => {
    if (!newDutyName.trim()) return;
    const id = newDutyName.trim().toLowerCase().replace(/\s+/g, '_');
    if (duties.find(d => d.id === id)) {
      setError('Aufgabe bereits vorhanden');
      return;
    }
    const colorIndex = duties.length % COLORS.length;
    setDuties([...duties, { 
      id, 
      name: newDutyName.trim(), 
      color: COLORS[colorIndex]
    }]);
    setNewDutyName('');
    setError('');
    setSchedule(null);
    setStats(null);
  };

  const removeDuty = (id) => {
    if (duties.length <= 1) {
      setError('Mindestens eine Aufgabe benötigt');
      return;
    }
    setDuties(duties.filter(d => d.id !== id));
    setSchedule(null);
    setStats(null);
  };

  const generateSchedule = () => {
    const minTrainees = duties.length * 2;
    if (trainees.length < minTrainees) {
      setError(`Mindestens ${minTrainees} Trainees benötigt (${duties.length} Aufgaben × 2 Personen)`);
      return;
    }

    setError('');
    
    // Track how many times each trainee has done each duty
    const traineeStats = {};
    trainees.forEach(t => {
      traineeStats[t] = {};
      duties.forEach(d => {
        traineeStats[t][d.id] = 0;
      });
      traineeStats[t].lastDuty = null;
      traineeStats[t].lastDay = -2;
      traineeStats[t].totalAssignments = 0;
    });

    const newSchedule = {};

    for (let dayIndex = 0; dayIndex < DAYS.length; dayIndex++) {
      const day = DAYS[dayIndex];
      newSchedule[day] = {};
      
      const usedToday = new Set();
      
      for (const duty of duties) {
        const assigned = [];
        
        // Score each trainee for this duty
        // Lower score = better candidate
        const candidates = trainees
          .filter(t => !usedToday.has(t))
          .map(t => {
            let score = 0;
            
            // Heavily penalize if same duty yesterday
            if (traineeStats[t].lastDuty === duty.id && traineeStats[t].lastDay === dayIndex - 1) {
              score += 1000;
            }
            
            // Prefer trainees who have done this duty less often (for fairness)
            score += traineeStats[t][duty.id] * 10;
            
            // Slight preference for those who worked less recently
            score += traineeStats[t].totalAssignments * 2;
            
            // Small random factor to avoid always same pattern
            score += Math.random() * 3;
            
            return { name: t, score };
          })
          .sort((a, b) => a.score - b.score);
        
        // Take best 2 candidates
        for (let i = 0; i < 2 && i < candidates.length; i++) {
          const trainee = candidates[i].name;
          assigned.push(trainee);
          usedToday.add(trainee);
          
          // Update stats
          traineeStats[trainee][duty.id]++;
          traineeStats[trainee].lastDuty = duty.id;
          traineeStats[trainee].lastDay = dayIndex;
          traineeStats[trainee].totalAssignments++;
        }
        
        newSchedule[day][duty.id] = assigned;
      }
    }
    
    setSchedule(newSchedule);
    
    // Calculate and show stats
    const statsSummary = {};
    trainees.forEach(t => {
      statsSummary[t] = {};
      duties.forEach(d => {
        statsSummary[t][d.id] = traineeStats[t][d.id];
      });
      statsSummary[t].total = traineeStats[t].totalAssignments;
    });
    setStats(statsSummary);
  };

  const downloadAsHTML = () => {
    if (!schedule) return;
    
    const html = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Dienstplan</title>
  <style>
    body { font-family: Arial, sans-serif; padding: 20px; }
    h1 { color: #1c2e52; text-align: center; }
    table { width: 100%; border-collapse: collapse; margin-top: 20px; }
    th, td { border: 1px solid #ddd; padding: 10px; text-align: center; }
    th { background-color: #1c2e52; color: white; }
    tr:nth-child(even) { background-color: #f9f9f9; }
    .duty-name { font-weight: bold; text-align: left; }
  </style>
</head>
<body>
  <h1>Dienstplan - Woche</h1>
  <table>
    <tr>
      <th>Aufgabe</th>
      ${DAYS.map(d => `<th>${d}</th>`).join('')}
    </tr>
    ${duties.map(duty => `
      <tr>
        <td class="duty-name">${duty.name}</td>
        ${DAYS.map(day => `
          <td>${schedule[day][duty.id]?.join('<br>') || '-'}</td>
        `).join('')}
      </tr>
    `).join('')}
  </table>
  ${stats ? `
  <h2 style="margin-top: 40px;">Verteilung pro Person</h2>
  <table>
    <tr>
      <th>Name</th>
      ${duties.map(d => `<th>${d.name}</th>`).join('')}
      <th>Gesamt</th>
    </tr>
    ${trainees.map(t => `
      <tr>
        <td><strong>${t}</strong></td>
        ${duties.map(d => `<td>${stats[t][d.id]}</td>`).join('')}
        <td><strong>${stats[t].total}</strong></td>
      </tr>
    `).join('')}
  </table>
  ` : ''}
  <p style="margin-top: 20px; color: #666; text-align: center;">
    Generiert am ${new Date().toLocaleDateString('de-DE')}
  </p>
</body>
</html>`;
    
    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Dienstplan_${new Date().toISOString().slice(0, 10)}.html`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const minTrainees = duties.length * 2;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#1c2e52]">
            <CalendarDays className="w-5 h-5 text-[#4b95d1]" />
            Dienstplan Generator
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Duties/Tasks Input */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <ClipboardList className="w-4 h-4" />
                Aufgaben ({duties.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex gap-2">
                <Input
                  placeholder="Neue Aufgabe hinzufügen"
                  value={newDutyName}
                  onChange={(e) => setNewDutyName(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && addDuty()}
                />
                <Button onClick={addDuty} className="bg-[#4b95d1] hover:bg-[#1c2e52]">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              
              <div className="flex flex-wrap gap-2">
                {duties.map((duty) => (
                  <Badge 
                    key={duty.id} 
                    variant="outline" 
                    className={`px-3 py-1 ${duty.color} flex items-center gap-2`}
                  >
                    {duty.name}
                    <button 
                      onClick={() => removeDuty(duty.id)}
                      className="hover:text-red-500"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Trainee Input */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Users className="w-4 h-4" />
                Trainees ({trainees.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex gap-2">
                <Input
                  placeholder="Name des Trainees"
                  value={newTraineeName}
                  onChange={(e) => setNewTraineeName(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && addTrainee()}
                />
                <Button onClick={addTrainee} className="bg-[#4b95d1] hover:bg-[#1c2e52]">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              
              {error && (
                <p className="text-sm text-red-500">{error}</p>
              )}
              
              <div className="flex flex-wrap gap-2">
                {trainees.map((name) => (
                  <Badge 
                    key={`trainee-${name}`} 
                    variant="secondary" 
                    className="px-3 py-1 bg-[#e6d7bf] text-[#1c2e52] flex items-center gap-2"
                  >
                    {name}
                    <button 
                      onClick={() => removeTrainee(name)}
                      className="hover:text-red-500"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>
              
              {trainees.length > 0 && trainees.length < minTrainees && (
                <p className="text-xs text-amber-600">
                  Noch {minTrainees - trainees.length} Trainee(s) benötigt (mindestens {minTrainees} für {duties.length} Aufgaben)
                </p>
              )}
            </CardContent>
          </Card>

          {/* Generate Button */}
          <div className="flex gap-2 justify-center">
            <Button 
              onClick={generateSchedule}
              disabled={trainees.length < minTrainees}
              className="bg-[#1c2e52] hover:bg-[#4b95d1]"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Dienstplan generieren
            </Button>
            
            {schedule && (
              <Button 
                onClick={downloadAsHTML}
                variant="outline"
                className="border-[#4b95d1] text-[#4b95d1] hover:bg-[#4b95d1] hover:text-white"
              >
                <Download className="w-4 h-4 mr-2" />
                Als HTML herunterladen
              </Button>
            )}
          </div>

          {/* Schedule Table */}
          {schedule && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Generierter Dienstplan</CardTitle>
              </CardHeader>
              <CardContent className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-[#1c2e52] text-white">
                      <th className="p-2 text-left rounded-tl-lg">Aufgabe</th>
                      {DAYS.map((day, idx) => (
                        <th key={day} className={`p-2 text-center ${idx === DAYS.length - 1 ? 'rounded-tr-lg' : ''}`}>
                          {day.slice(0, 2)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {duties.map((duty, dutyIdx) => (
                      <tr key={duty.id} className={dutyIdx % 2 === 0 ? 'bg-gray-50' : ''}>
                        <td className="p-2 border-b">
                          <div className={`flex items-center gap-2 px-2 py-1 rounded border ${duty.color}`}>
                            <span className="text-xs sm:text-sm">{duty.name}</span>
                          </div>
                        </td>
                        {DAYS.map(day => (
                          <td key={day} className="p-2 border-b text-center">
                            <div className="flex flex-col gap-1">
                              {schedule[day][duty.id]?.map((name, i) => (
                                <span 
                                  key={i} 
                                  className="text-xs bg-white px-2 py-1 rounded border shadow-sm"
                                >
                                  {name}
                                </span>
                              ))}
                            </div>
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          )}

          {/* Statistics */}
          {stats && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Verteilung pro Person (Gleichmäßigkeit)</CardTitle>
              </CardHeader>
              <CardContent className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="p-2 text-left">Name</th>
                      {duties.map(d => (
                        <th key={d.id} className="p-2 text-center text-xs">{d.name.split(' ')[0]}</th>
                      ))}
                      <th className="p-2 text-center font-bold">Gesamt</th>
                    </tr>
                  </thead>
                  <tbody>
                    {trainees.map((t, idx) => (
                      <tr key={t} className={idx % 2 === 0 ? 'bg-gray-50' : ''}>
                        <td className="p-2 font-medium">{t}</td>
                        {duties.map(d => (
                          <td key={d.id} className="p-2 text-center">
                            <span className={`inline-block w-6 h-6 rounded-full leading-6 text-xs ${
                              stats[t][d.id] === 0 ? 'bg-gray-200 text-gray-500' :
                              stats[t][d.id] >= 3 ? 'bg-green-100 text-green-700' :
                              'bg-blue-100 text-blue-700'
                            }`}>
                              {stats[t][d.id]}
                            </span>
                          </td>
                        ))}
                        <td className="p-2 text-center font-bold text-[#1c2e52]">{stats[t].total}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="text-xs text-gray-500 mt-3 text-center">
                  Die Verteilung ist möglichst gleichmäßig. Klicken Sie erneut auf &quot;Generieren&quot; für eine andere Variante.
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DienstplanGenerator;
